import type { AssetDetail } from "@/entities/asset";
import type { AssetTechnicalFormValues } from "@/modules/asset/asset-technical-form";

import { Form } from "antd";
import first from "lodash/first";
import isEqual from "lodash/isEqual";
import { useEffect } from "react";

import {
  getAssetCleanTechnicalData,
  getAssetValidTechnicalData,
  useAssetUpdate,
} from "@/entities/asset";
import { useGetEntityTemplate } from "@/entities/entity-template";
import { useSaveBar } from "@/modules/app-save-bar";

export const useAssetTechnicalDataUpdate = (asset?: AssetDetail) => {
  const [form] = Form.useForm();
  const { setIsDirty } = useSaveBar();
  const { mutate, isPending } = useAssetUpdate(asset?.id || 0);
  const initialTechnicalData = getAssetValidTechnicalData(
    asset?.technical_data
  );
  const lastTechnicalUpdate = first(asset?.technical_data_updates || []);

  const { isLoading, schema } = useGetEntityTemplate(
    asset?.asset_type?.entity_template_id ?? 0
  );
  useEffect(() => {
    if (initialTechnicalData) {
      form.setFieldsValue(initialTechnicalData);
    }
  }, [form, initialTechnicalData]);

  const onCancel = () => {
    form.setFieldsValue(initialTechnicalData);
    setIsDirty(false);
  };

  const handleDataChange = (values: AssetTechnicalFormValues) => {
    const cleanedValues = getAssetCleanTechnicalData(values);
    setIsDirty(!isEqual(cleanedValues, initialTechnicalData));
  };

  const onSave = () => {
    const technicalData = getAssetCleanTechnicalData(form.getFieldsValue());

    mutate(
      { technical_data: technicalData },
      {
        onSuccess: function () {
          setIsDirty(false);
        },
      }
    );
  };

  return {
    schema,
    onSave,
    onCancel,
    isLoading: isPending || isLoading,
    handleDataChange,
    form,
    lastTechnicalUpdate,
  };
};
